import axios from 'axios';
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import Konva from 'konva';

export function inIframe() {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
}

export const axiosInstance = axios.create({
    withCredentials: true,
    baseURL: process.env.API_URL,
});

export const API_CODY_URL = `${process.env.API_URL}/cody2`;


export function getLabel(
    project: ProjectConfiguration,
    feature: BaseNode,
    label: string | undefined = undefined,
) {
    let formattedLabel = label ?? feature.name;
    const global_vars = project.getVariables();
    const exp = /\{\{\$(.*?)\}\}/g;
    let match = exp.exec(label as string);
    while (match != null) {
        const varName = match[1];
        let found = false;
        for (let j = 0; j < global_vars.length; j += 1) {
            if (global_vars[j].getID() === varName) {
                formattedLabel = formattedLabel.replace(match[0], `<span class="block-inline block-inline-variable">${global_vars[j].getSettingValue('VARNAME')}</span>`);
                found = true;
            }
        }
        if (feature.id.indexOf('node_') === 0) {
            const inputs = feature.getInputs();
            for (let i = 0; i < inputs.length; i++) {
                const links = project.getNodeLinks(feature, inputs[i].portId);
                if (links && links.length > 0) {
                    const link = links[0];
                    const source = project.getFeature(link.getSettingValue('SOURCE_FEATURE')) as BaseNode;
                    const sourcePort = source.getPort(link.getSettingValue('SOURCE_PORT'));
                    if (sourcePort) {
                        const sourceParams = sourcePort.params;
                        for (let j = 0; j < sourceParams.length; j++) {
                            if (sourceParams[j].name === varName) {
                                formattedLabel = formattedLabel.replace(match[0], `<span class="block-inline block-inline-parameter">${sourceParams[j].name}</span>`);
                                found = true;
                            }
                        }
                    }
                }
            }
        }
        if (!found) {
            // try to get feature
            const linkedFeature = project.getFeature(varName);
            if (linkedFeature) {
                formattedLabel = formattedLabel.replace(match[0], `<span class="block-inline block-inline-feature">${linkedFeature.getName()}</span>`);
                found = true;
            }
        }

        if (!found) {
            formattedLabel = formattedLabel.replace(match[0], `<span class="block-inline block-inline-parameter">${match[1]}</span>`);
        }
        match = exp.exec(label as string);
    }
    return formattedLabel;
}


export function floatToBytes(fnum: number): string {
    const arr = new ArrayBuffer(4);
    const view = new DataView(arr);
    view.setFloat32(0, fnum, false);
    let result = '';
    for (let i = 0; i < arr.byteLength; i++) {
        result += toHexString(view.getUint8(i));
    }
    return result;
}

export function parseFloat(str: string) {
    let float = 0;
    let exp;
    let int = 0;
    let multi = 1;
    if (/^0x/.exec(str)) {
        int = parseInt(str, 16);
    } else {
        for (let i = str.length - 1; i >= 0; i -= 1) {
            if (str.charCodeAt(i) > 255) {
                return false;
            }
            int += str.charCodeAt(i) * multi;
            multi *= 256;
        }
    }
    const sign = (int >>> 31) ? -1 : 1;
    if (int !== 0) {
        exp = (int >>> 23);
        exp = (exp & 0xff) - 127;
        const mantissa = ((int & 0x7fffff) + 0x800000).toString(2);
        for (let i = 0; i < mantissa.length; i += 1) {
            float += parseInt(mantissa[i], 10) ? Math.pow(2, exp) : 0;
            exp--;
        }
    }
    return float * sign;
}

export function toHexString(input: any) {
    return Number(input).toString(16).padStart(2, '0');
}

export function makeid(length: number) {
    let result = '';
    const characters = '0123456789abcde';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
}

export function binToBase64(bin: ArrayBuffer) {
    let binary = '';
    const bytes = new Uint8Array(bin);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
}

export function base64ToBin(base64: string) {
    const binStr = window.atob(base64);
    const len = binStr.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binStr.charCodeAt(i);
    }
    return bytes.buffer;
}

export function getFeatureDocumentationTag(featureId: string) {
    switch (featureId) {
        case 'cron':
            return 'schedules';
        case 'stg':
            return 'settings';
        case 'tbl':
            return 'tables';
        default:
            return featureId;
    }
}

export function convertFilterFormat(filters: any) {
    const parsedFilter: any = {};
    for (let i = 0; i < filters.length; i++) {
        const filter = filters[i];
        let key = filter.field;
        let operator = filter.operator;
        switch (filter.operator) {
            case '==':
                operator = '___eq';
                break;
            case '!=':
                operator = '___ne';
                break;
            case '>':
                operator = '___gt';
                break;
            case '<':
                operator = '___lt';
                break;
            case '>=':
                operator = '___gte';
                break;
            case '<=':
                operator = '___lte';
                break;
            default:

                break;
        }
        key += operator;
        parsedFilter[key] = filter.value;
        if (filter.value === null) {
            parsedFilter[key] = '';
        }
    }
    return parsedFilter;
}

export function getSvgViewbox(
    svgElement: ReactDOM.SVGElement, margin: number = 0,
) {
    let svgWidth;
    let svgHeight;
    let currElement = svgElement;
    while (!svgWidth || !svgHeight) {
        if (!currElement) {
            break;
        }
        currElement = currElement.firstChild;
        if (!currElement || currElement.getAttribute === undefined) {
            continue;
        }
        if (currElement.getAttribute('width')) {
            svgWidth = parseInt(currElement.getAttribute('width') as string, 10);
        }
        if (currElement.getAttribute('height')) {
            svgHeight = parseInt(currElement.getAttribute('height') as string, 10);
        }
    }
    if (!svgWidth || !svgHeight) {
        console.warn('SVG element does not have width and height attributes');
        return '0 0 0 0';
    }

    const initZoomOut = 1.2;

    const height = Math.round(svgHeight * initZoomOut);
    const width = Math.round(svgWidth * initZoomOut) + margin;
    const minX = (svgWidth - width) / 2;
    const minY = (svgHeight - height) / 2;

    return `${minX} ${minY} ${width} ${height}`;
}

enum HeaderTypes {
    BITMAP_INFO_HEADER = 40,
    BITMAP_V2_INFO_HEADER = 52,
    BITMAP_V3_INFO_HEADER = 56,
    BITMAP_V4_HEADER = 108,
    BITMAP_V5_HEADER = 124
}

type BitsPerPixel = 1 | 4 | 8 | 16 | 24 | 32;

interface IColor {
    red: number;
    green: number;
    blue: number;
    quad: number;
}

interface IDecoderOptions {
    toRGBA?: boolean;
}

interface IImage {
    width: number;
    height: number;
    bitPP: BitsPerPixel;
    data: Uint8Array;
    reserved1?: number;
    reserved2?: number;
    hr?: number;
    vr?: number;
    colors?: number;
    importantColors?: number;
    palette?: IColor[];
}

const enum Compression {
    NONE = 0,
    BI_RLE8 = 1,
    BI_RLE4 = 2,
    BI_BIT_FIELDS = 3,
    BI_ALPHA_BIT_FIELDS = 6
}

export interface IBitmapImage extends IImage {
    flag: string;
    fileSize: number;
    offset: number;
    headerSize: number;
    planes?: number;
    compression?: Compression;
    rawSize: number;
}

type IColorProcessor = (p: number, i: number, x: number, y: number) => number;

function createInteger(numbers: number[]) {
    return numbers.reduce((final, n) => (final << 1) | n, 0);
}

function createColor(color: IColor): number {
    return (
        (color.quad << 24) | (color.red << 16) | (color.green << 8) | color.blue
    );
}

export class BmpEncoder implements IBitmapImage {
    public readonly fileSize: number;

    public readonly reserved1: number;

    public readonly reserved2: number;

    public readonly offset: number;

    public readonly width: number;

    public readonly flag: string;

    public readonly height: number;

    public readonly planes: number;

    public readonly bitPP: BitsPerPixel;

    public readonly compress: number;

    public readonly hr: number;

    public readonly vr: number;

    public readonly colors: number;

    public readonly importantColors: number;

    public readonly rawSize: number;

    public readonly headerSize: number;

    public readonly data: Uint8Array;

    public readonly palette: IColor[];

    private readonly extraBytes: number;

    private readonly buffer: Uint8Array;

    private readonly dataView: DataView;

    private readonly bytesInColor: number;

    private pos: number;

    constructor(imgData: IImage) {
        this.buffer = imgData.data;
        this.width = imgData.width;
        this.height = imgData.height;
        this.headerSize = HeaderTypes.BITMAP_INFO_HEADER;

        // Header
        this.flag = 'BM';
        this.bitPP = imgData.bitPP;
        this.offset = 54;
        this.reserved1 = imgData.reserved1 || 0;
        this.reserved2 = imgData.reserved2 || 0;
        this.planes = 1;
        this.compress = 0;
        this.hr = imgData.hr || 0;
        this.vr = imgData.vr || 0;
        this.importantColors = imgData.importantColors || 0;
        this.colors = 256;
        this.palette = imgData.palette || [];

        if (this.colors && this.bitPP < 16) {
            this.offset += this.colors * 4;
        } else {
            this.colors = 0;
        }

        switch (this.bitPP) {
            case 32:
                this.bytesInColor = 4;
                break;
            case 16:
                this.bytesInColor = 2;
                break;
            case 8:
                this.bytesInColor = 1;
                break;
            case 4:
                this.bytesInColor = 1 / 2;
                break;
            case 1:
                this.bytesInColor = 1 / 8;
                break;
            default:
                this.bytesInColor = 3;
                this.bitPP = 24;
        }

        const rowWidth = (this.width * this.bitPP) / 32;
        const rowBytes = Math.ceil(rowWidth);

        this.extraBytes = (rowBytes - rowWidth) * 4;
        // Why 2?
        this.rawSize = this.height * rowBytes * 4 + 2;
        this.fileSize = this.rawSize + this.offset;
        this.data = new Uint8Array(this.fileSize);
        this.dataView = new DataView(this.data.buffer);
        this.data.fill(1);
        this.pos = 0;

        this.encode();
    }

    public encode() {
        this.pos = 0;

        this.writeHeader();

        switch (this.bitPP) {
            case 32:
                this.bit32();
                break;
            case 16:
                this.bit16();
                break;
            case 8:
                this.bit8();
                break;
            case 4:
                this.bit4();
                break;
            case 1:
                this.bit1();
                break;
            default:
                this.bit24();
        }
    }

    private writeHeader() {
        this.data[this.pos++] = this.flag.charCodeAt(0);
        this.data[this.pos++] = this.flag.charCodeAt(1);

        this.writeUInt32LE(this.fileSize);

        // Writing 2 UInt16LE resulted in a weird bug
        this.writeUInt32LE((this.reserved1 << 16) | this.reserved2);

        this.writeUInt32LE(this.offset);
        this.writeUInt32LE(this.headerSize);
        this.writeUInt32LE(this.width);
        this.writeUInt32LE(this.height);

        this.dataView.setUint16(this.pos, this.planes, true);
        this.pos += 2;

        this.dataView.setUint16(this.pos, this.bitPP, true);
        this.pos += 2;

        this.writeUInt32LE(this.compress);
        this.writeUInt32LE(this.rawSize);
        this.writeUInt32LE(this.hr);
        this.writeUInt32LE(this.vr);
        this.writeUInt32LE(this.colors);
        this.writeUInt32LE(this.importantColors);
    }

    private bit1() {
        if (this.palette.length && this.colors === 2) {
            this.initColors(1);
        } else {
            this.writeUInt32LE(0x00ffffff); // Black
            this.writeUInt32LE(0x00000000); // White
        }

        this.pos += 1; // ?

        let lineArr: number[] = [];

        this.writeImage((p, index, x) => {
            let i = index;

            i++;
            const b = this.buffer[i++];
            const g = this.buffer[i++];
            const r = this.buffer[i++];

            const brightness = r * 0.2126 + g * 0.7152 + b * 0.0722;

            lineArr.push(brightness > 127 ? 0 : 1);

            if ((x + 1) % 8 === 0) {
                this.data[p - 1] = createInteger(lineArr);
                lineArr = [];
            } else if (x === this.width - 1 && lineArr.length > 0) {
                this.data[p - 1] = createInteger(lineArr) << 4;
                lineArr = [];
            }

            return i;
        });
    }

    private bit4() {
        const colors = this.initColors(4);

        let integerPair: number[] = [];

        this.writeImage((p, index, x) => {
            let i = index;

            const colorInt = createColor({
                quad: this.buffer[i++],
                blue: this.buffer[i++],
                green: this.buffer[i++],
                red: this.buffer[i++],
            });
            const colorExists = colors.findIndex(c => c === colorInt);

            if (colorExists !== -1) {
                integerPair.push(colorExists);
            } else {
                integerPair.push(0);
            }

            if ((x + 1) % 2 === 0) {
                this.data[p] = (integerPair[0] << 4) | integerPair[1];
                integerPair = [];
            }

            return i;
        });
    }

    private bit8() {
        const colors = this.initColors(8);

        this.writeImage((p, index) => {
            let i = index;

            const colorData = {
                red: this.buffer[i++],
                green: this.buffer[i++],
                blue: this.buffer[i++],
                quad: this.buffer[i++],
            };

            const colorInt = createColor(colorData);
            // const colorExists = colors.findIndex(c => c === colorInt);
            const colorExists = this.findClosestColor(colorData);

            if (colorExists !== -1) {
                this.data[p] = colorExists;
            } else {
                this.data[p] = 0;
            }

            return i;
        });
    }

    private bit16() {
        this.writeImage((p, index) => {
            let i = index + 1;

            const b = this.buffer[i++] / 8; // b
            const g = this.buffer[i++] / 8; // g
            const r = this.buffer[i++] / 8; // r

            const color = (r << 10) | (g << 5) | b;

            this.data[p] = color & 0x00ff;
            this.data[p + 1] = (color & 0xff00) >> 8;

            return i;
        });
    }

    private bit24() {
        this.writeImage((p, index) => {
            let i = index + 1;

            this.data[p] = this.buffer[i++]; // b
            this.data[p + 1] = this.buffer[i++]; // g
            this.data[p + 2] = this.buffer[i++]; // r

            return i;
        });
    }

    private bit32() {
        this.writeImage((p, index) => {
            let i = index;

            this.data[p + 3] = this.buffer[i++]; // a
            this.data[p] = this.buffer[i++]; // b
            this.data[p + 1] = this.buffer[i++]; // g
            this.data[p + 2] = this.buffer[i++]; // r

            return i;
        });
    }

    private writeImage(writePixel: IColorProcessor) {
        const rowBytes = this.extraBytes + this.width * this.bytesInColor;

        let i = 0;

        for (let y = 0; y < this.height; y++) {
            for (let x = 0; x < this.width; x++) {
                const p = Math.floor(
                    this.pos + (this.height - 1 - y) * rowBytes + x * this.bytesInColor,
                );

                i = writePixel.call(this, p, i, x, y);
            }
        }
    }

    private initColors(bit: number) {
        const colors: number[] = [];

        if (this.palette.length) {
            for (let i = 0; i < this.colors; i++) {
                const rootColor = createColor(this.palette[i]);
                this.writeUInt32LE(rootColor);
                colors.push(rootColor);
            }
        } else {
            throw new Error(
                `To encode ${bit}-bit BMPs a pallette is needed. Please choose up to ${this.colors
                } colors. Colors must be 32-bit integers.`,
            );
        }

        return colors;
    }

    private findClosestColor(color: IColor) {
        let index = 0;
        let min = 1000000000;

        for (let i = 0; i < this.palette.length; i++) {
            const c = this.palette[i];
            const diff = Math.abs(c.red - color.red)
                + Math.abs(c.green - color.green)
                + Math.abs(c.blue - color.blue);

            if (diff < min) {
                min = diff;
                index = i;
            }
        }

        return index;
    }

    private writeUInt32LE(value: number) {
        this.dataView.setUint32(this.pos, value, true);
        this.pos += 4;
    }
}

export const NODE_PORT_CONNECTION_WIDTH = 40;
export const NODE_PORT_CONNECTION_HEIGHT = 26;

export function drawCharacter(ctx: Konva.Context, pixels: number[][], width: number, x: number, y: number, color = '#000') {
    let r = 0;
    let g = 0;
    let b = 0;
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
    if (result) {
        r = parseInt(result[1], 16);
        g = parseInt(result[2], 16);
        b = parseInt(result[3], 16);
    }
    const glyphCanvas = document.createElement('canvas');
    glyphCanvas.width = width;
    glyphCanvas.height = pixels.length;
    const glyphCtx = glyphCanvas.getContext('2d');
    if (!glyphCtx) {
        return;
    }
    const imageData = ctx.createImageData(width, pixels.length);
    if (!imageData) {
        return;
    }
    const data = imageData.data;
    for (let pixelY = 0; pixelY < pixels.length; pixelY++) {
        for (let pixelX = 0; pixelX < Math.min(width, pixels[pixelY].length); pixelX++) {
            const index = (pixelY * width + pixelX) * 4;
            if (pixels[pixelY][pixelX]) {
                data[index] = r;
                data[index + 1] = g;
                data[index + 2] = b;
                data[index + 3] = 255;
            } else {
                data[index + 3] = 0;
            }
        }
    }
    glyphCtx.putImageData(imageData, 0, 0);
    ctx.drawImage(glyphCanvas, x, y);
}

/**
 * Draws a whole string with adjustable tracking.
 */
export function drawString(
    ctx: Konva.Context,
    widget: {
        TEXT: string;
        COLOR: string;
        BACKGROUND_COLOR: string;
        W: number;
        H: number;
        VISIBLE: number;
        ALIGNMENT: string;
        FONT_HEIGHT?: number;
    },
    font: {
        characters: any[],
        header: {
            height: number,
        },
    },
    screenWidth: number,
    screenHeight: number,
    featureId: string,
    tracking = 1,
) {
    const {
        TEXT, COLOR, BACKGROUND_COLOR, W, H, VISIBLE, ALIGNMENT,
        FONT_HEIGHT,
    } = widget;
    // X and Y already set in shape
    const X = 0;
    const Y = 0;
    let y = Y + tracking;
    if (!VISIBLE) {
        return;
    }
    if (!font) {
        const height = FONT_HEIGHT || 12;
        ctx.fillStyle = COLOR;
        ctx.font = `${height}px montserrat`;
        ctx.fillText(TEXT, X, Y + height);
        return;
    }
    const { characters, header } = font;
    const height = header.height;
    let currWidth = 0;
    let charactersToPrint: {
        pixels: number[][],
        y: number,
        width: number,
    }[] = [];
    const lines: {
        characters: typeof charactersToPrint,
        width: number,
        height: number,
    }[] = [];
    switch (ALIGNMENT) {
        case 'PL_LCD_TEXT_ALIGNMENT_TOP_LEFT':
        case 'PL_LCD_TEXT_ALIGNMENT_BOTTOM_LEFT':
        case 'PL_LCD_TEXT_ALIGNMENT_MIDDLE_LEFT':
            {
                // First pass - collect lines
                for (let i = 0; i < TEXT.length; i++) {
                    const ch = TEXT[i];
                    const char = characters.find((c: any) => c.charCode === ch.charCodeAt(0));
                    if (!char) {
                        continue;
                    }
                    const { pixels, width, height } = char;
                    if (currWidth + width > Math.min(X + W, screenWidth) && featureId !== 'ssd1306') {
                        break;
                        // lines.push({
                        //     characters: charactersToPrint,
                        //     width: currWidth,
                        //     height,
                        // });
                        // currWidth = 0;
                        // charactersToPrint = [];
                        // y += height;
                    }
                    if (ch === '`') {
                        if (charactersToPrint.length > 0) {
                            lines.push({
                                characters: charactersToPrint,
                                width: currWidth,
                                height,
                            });
                        }
                        currWidth = 0;
                        charactersToPrint = [];
                        y += height;
                        continue;
                    }
                    if (pixels) {
                        currWidth += width + tracking;
                        charactersToPrint.push({
                            pixels,
                            y,
                            width,
                        });
                    }
                }

                if (charactersToPrint.length > 0) {
                    lines.push({
                        characters: charactersToPrint,
                        width: currWidth,
                        height,
                    });
                }

                // Calculate total height and vertical offset
                const totalHeight = lines.reduce((sum, line) => sum + line.height, 0);
                const yOffset = (H - totalHeight) / 2;

                // Second pass - dra/w with vertical centering
                let currentY = Y + yOffset;
                for (let i = 0; i < lines.length; i++) {
                    const { characters, height } = lines[i];
                    let x = X;
                    for (let j = 0; j < characters.length; j++) {
                        const { pixels, width } = characters[j];
                        drawCharacter(ctx, pixels, width, x, currentY, COLOR);
                        x += width + tracking;
                    }
                    currentY += height;
                }
            }
            break;
        case 'PL_LCD_TEXT_ALIGNMENT_TOP_CENTER':
        case 'PL_LCD_TEXT_ALIGNMENT_BOTTOM_CENTER':
        case 'PL_LCD_TEXT_ALIGNMENT_MIDDLE_CENTER':
            {
                // First pass - collect lines
                for (let i = 0; i < TEXT.length; i++) {
                    const ch = TEXT[i];
                    const char = characters.find((c: any) => c.charCode === ch.charCodeAt(0));
                    if (!char) {
                        continue;
                    }
                    const { pixels, width } = char;
                    if (currWidth + width + tracking >= W) {
                        break;
                    }
                    if (ch === '`') {
                        y += height;
                        lines.push({
                            characters: charactersToPrint,
                            width: currWidth,
                            height,
                        });
                        currWidth = 0;
                        charactersToPrint = [];
                        if (y + height > H) {
                            break;
                        }
                        continue;
                    }
                    if (pixels) {
                        currWidth += width + tracking;
                    }
                    charactersToPrint.push({
                        pixels,
                        y,
                        width,
                    });
                }

                if (charactersToPrint.length > 0) {
                    lines.push({
                        characters: charactersToPrint,
                        width: currWidth,
                        height,
                    });
                }

                // Calculate total height of all lines
                const totalHeight = lines.reduce((sum, line) => sum + line.height, 0);
                const yOffset = (H - totalHeight) / 2;

                // Adjust y position for each line relative to the center
                let currentY = Y + yOffset;
                for (let i = 0; i < lines.length; i++) {
                    const { characters, width: lineWidth } = lines[i];
                    let x = X + Math.max(0, (W - lineWidth) / 2);
                    for (let j = 0; j < characters.length; j++) {
                        const { pixels, width } = characters[j];
                        drawCharacter(ctx, pixels, width, x, currentY, COLOR);
                        x += width + tracking;
                    }
                    currentY += lines[i].height;
                }
            }
            break;
        case 'PL_LCD_TEXT_ALIGNMENT_TOP_RIGHT':
        case 'PL_LCD_TEXT_ALIGNMENT_BOTTOM_RIGHT':
        case 'PL_LCD_TEXT_ALIGNMENT_MIDDLE_RIGHT':
            {
                // First pass - collect lines
                for (let i = 0; i < TEXT.length; i++) {
                    const ch = TEXT[i];
                    const char = characters.find((c: any) => c.charCode === ch.charCodeAt(0));
                    if (!char) {
                        continue;
                    }
                    const { pixels, width } = char;
                    if (pixels) {
                        currWidth += width + tracking;
                    }
                    if (ch === '`') {
                        if (charactersToPrint.length > 0) {
                            lines.push({
                                characters: charactersToPrint,
                                width: currWidth,
                                height,
                            });
                        }
                        currWidth = 0;
                        charactersToPrint = [];
                        y += height;
                        continue;
                    }
                    if (currWidth >= W) {
                        break;
                        // lines.push({
                        //     characters: charactersToPrint,
                        //     width: currWidth
                        //         - (width + tracking), // Remove width of current char
                        //     height,
                        // });
                        // currWidth = width + tracking;
                        // charactersToPrint = [];
                        // y += height;
                    }
                    if (pixels) {
                        charactersToPrint.push({
                            pixels,
                            y,
                            width,
                        });
                    }
                }

                if (charactersToPrint.length > 0) {
                    lines.push({
                        characters: charactersToPrint,
                        width: currWidth,
                        height,
                    });
                }

                // Calculate total height and vertical offset
                const totalHeight = lines.reduce((sum, line) => sum + line.height, 0);
                const yOffset = (H - totalHeight) / 2;

                // Second pass - draw with vertical centering
                let currentY = Y + yOffset;
                for (let i = 0; i < lines.length; i++) {
                    const { characters } = lines[i];
                    let x = X + W; // Start from the right edge
                    for (let j = characters.length - 1; j >= 0; j--) {
                        const { pixels, width } = characters[j];
                        x -= (width + tracking);
                        drawCharacter(ctx, pixels, width, x, currentY, COLOR);
                    }
                    currentY += height;
                }
            }
            break;
        default:
    }
}
